import { useState } from 'react';
import { ArrowUpRight, ArrowRight, ChevronDown, ChevronRight, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollReveal } from '../hooks/useScrollReveal';
import Footer from './Footer';
import { cn } from '@/lib/utils';

// ─── Data ────────────────────────────────────────────────────────────────────

interface Product {
  id: string;
  number: string;
  name: string;
  tagline: string;
  description: string;
  features: string[];
  link?: { label: string; href: string };
  appStoreUrl?: string;
  metrics?: { label: string; value: string }[];
}

interface MortgageProject {
  name: string;
  summary: string;
  milestone: string;
  roi: string;
}

interface IndustryProcess {
  name: string;
  detail: string;
}

interface Industry {
  id: string;
  number: string;
  name: string;
  tagline: string;
  description: string;
  features: string[];
  metrics: { label: string; value: string }[];
  subProjects?: { stage: string; projects: MortgageProject[] }[];
  processes?: IndustryProcess[];
}

interface Agent {
  id: string;
  number: string;
  name: string;
  tagline: string;
  description: string;
  features: string[];
  metrics: { label: string; value: string }[];
}

const faqData = [
  {
    question: 'What is Antz AI and what does it do?',
    answer:
      'Antz AI builds AI systems that automate real operational work. We ship products and custom agents that reduce manual effort, cut errors, and speed up execution in areas like compliance, search, and decisioning.',
  },
  {
    question: 'How is Antz AI different from other AI companies?',
    answer:
      'We focus on outcomes, not demos. We help pick the right use cases, ship into your workflow, and keep iterating until it delivers measurable value.',
  },
  {
    question: 'How does Antz AI partner with clients?',
    answer:
      'We work as an extension of your team from discovery through rollout. After launch, we keep improving the system based on real production usage.',
  },
  {
    question: 'What industries and workflows does Antz AI specialize in?',
    answer:
      'We work in enterprise environments where reliability, accuracy, and governance matter. This includes mortgage automation, manufacturing intelligence, enterprise search, and workflow automation across ops teams.',
  },
  {
    question: 'Does Antz AI offer custom AI solutions or ready-made products?',
    answer:
      'Both. We offer enterprise products (like governance and search), and we build custom solutions when the work is unique to your process or industry requirements.',
  },
  {
    question: 'Is Antz AI suitable for enterprise automation?',
    answer:
      'Yes. We build for security, compliance, and scale, with a focus on measurable value rather than one-off experiments.',
  },
  {
    question: 'Why is it called Antz AI?',
    answer:
      'Ants are small, coordinated workers that get a lot done together. That is how we approach enterprise AI: a set of systems and agents that coordinate to move work forward.',
  },
];

const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-overlay/[0.06] last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-4 py-5 text-left group"
      >
        <span className="text-[15px] font-medium text-foreground group-hover:text-primary transition-colors">
          {question}
        </span>
        <ChevronDown
          size={18}
          className={cn(
            'text-muted-foreground flex-shrink-0 transition-transform duration-300',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-300',
          isOpen ? 'max-h-60 pb-5' : 'max-h-0'
        )}
      >
        <p className="text-[14px] text-muted-foreground leading-relaxed pr-8">
          {answer}
        </p>
      </div>
    </div>
  );
};

// ─── Industry Data ───────────────────────────────────────────────────────────

const mortgageProjects: MortgageProject[] = [
  { name: 'Suspense Automation', summary: 'Auto-retrieves investor portal exception reports and syncs them into Encompass LOS for faster resolution.', milestone: 'Post-Closing', roi: 'Accelerates loan sale to secondary market and reduces aged-suspense penalties.' },
  { name: '3DI (Day 2 and Day 3 Disclosures)', summary: 'Automates delivery of initial and revised Loan Estimates to maintain TRID compliance.', milestone: 'Origination', roi: 'Speeds path to closing and eliminates costly compliance violations.' },
  { name: 'MERS', summary: 'Reconciles Encompass data with MERS registry to ensure accurate servicing rights tracking.', milestone: 'Post-Closing', roi: 'Ensures smooth investor transfers and prevents lien-ownership disputes.' },
  { name: 'HFA (Housing Finance Agency)', summary: 'Auto-updates conditions for state-level HFA program loans with unique compliance requirements.', milestone: 'Processing', roi: 'Prevents underwriting delays from unmet HFA-specific conditions.' },
  { name: 'Soft Credit', summary: 'Removes expired soft credit pull reports from Encompass to maintain clean loan files.', milestone: 'Servicing', roi: 'Automates file hygiene and reduces administrative overhead.' },
  { name: 'ECOA Withdrawal', summary: 'Automates required adverse action notices for withdrawn applications within federal deadlines.', milestone: 'Processing', roi: 'Eliminates risk of ECOA regulatory fines for missed notice deadlines.' },
  { name: 'ECOA Credit Denial', summary: 'Extracts adverse action factors from credit reports to auto-generate denial notices.', milestone: 'Processing', roi: 'Reduces manual work and ensures timely, accurate denial letters.' },
  { name: 'HMDA', summary: 'Automates HMDA LAR compilation by cross-referencing internal reports for regulatory submission.', milestone: 'Post-Closing', roi: 'Eliminates manual HMDA validation effort and avoids submission penalties.' },
  { name: 'Post Audit', summary: 'Processes post-closing QC files by uploading attachments to correct Encompass loan records.', milestone: 'Post-Closing', roi: 'Ensures audit-ready files and accelerates investor delivery.' },
  { name: 'Final Docs \u2013 First American Sync', summary: "Syncs trailing document status from First American's portal into Encompass automatically.", milestone: 'Post-Closing', roi: 'Speeds collateral file delivery and reduces warehouse line interest.' },
  { name: 'Final Docs \u2013 Investor Exceptions', summary: 'Downloads and tracks investor exception reports for proactive post-closing resolution.', milestone: 'Post-Closing', roi: 'Prevents buyback demands through faster exception clearance.' },
  { name: 'Final Docs Report', summary: 'Real-time dashboard consolidating trailing documents and investor exceptions for the post-closing team.', milestone: 'Post-Closing', roi: 'Increases loan sale throughput with faster pipeline visibility.' },
  { name: 'Post CD (Closing Disclosure)', summary: 'Automates the stare-and-compare QC check between the Post-CD document and Encompass data.', milestone: 'Closing', roi: 'Prevents funding errors and avoids re-disclosure costs.' },
  { name: 'Loan Scenario Documents', summary: 'Auto-generates preliminary Loan Estimates and fee worksheets for borrower comparison.', milestone: 'Origination', roi: 'Helps borrowers decide faster and frees loan officer time.' },
  { name: 'Collateral Information Tracking', summary: 'Tracks mortgage notes sent to Warehouse Banks for credit facility and liquidity management.', milestone: 'Post-Closing', roi: 'Reduces warehouse line carry costs and optimizes cash flow.' },
  { name: 'Pre-Close Automation', summary: 'Automates loan selection for pre-closing QC audits per investor and regulatory requirements.', milestone: 'Closing', roi: 'Prevents defective originations and costly investor buybacks.' },
  { name: 'Lease Management System', summary: 'Manages lease and facility data for distributed mortgage operations across multiple states.', milestone: 'General Operations', roi: 'Lowers operational overhead to improve per-loan profitability.' },
  { name: 'Polly Repricing', summary: 'Automates locked-loan repricing through Polly PPE when terms change in volatile rate environments.', milestone: 'Origination', roi: 'Provides instant accurate pricing and protects profit margins.' },
  { name: 'Polly Proxy API', summary: 'Simplified API exposing essential Polly pricing data for faster loan officer rate quotes.', milestone: 'Origination', roi: 'Boosts loan officer efficiency and borrower response speed.' },
  { name: 'Encompass Smartsheet Integration', summary: 'Bi-directional sync between Encompass and Smartsheet for branch pipeline and pricing visibility.', milestone: 'Origination', roi: 'Reduces pipeline bottlenecks and accelerates the loan sale process.' },
];

function groupByStage(projects: MortgageProject[]) {
  const stages = ['Origination', 'Processing', 'Closing', 'Post-Closing', 'Servicing', 'General Operations'];
  return stages
    .map((stage) => ({
      stage,
      projects: projects.filter((p) => p.milestone === stage),
    }))
    .filter((g) => g.projects.length > 0);
}

const industries: Industry[] = [
  {
    id: 'mortgage',
    number: '/0.1',
    name: 'Mortgage Automation',
    tagline: 'End-to-end mortgage lifecycle intelligence',
    description:
      'Navigate the complex, regulated US mortgage landscape with efficiency and compliance through intelligent automation. 19 purpose-built agents cover every stage from origination to servicing.',
    features: [
      'Compliance with RESPA, TILA, TRID, and more',
      'Reduced time to fund and sell',
      'Cost reduction and profit maximization',
    ],
    metrics: [
      { label: 'Cost to Produce', value: '-25%' },
      { label: 'Turn Time', value: '-30%' },
      { label: 'Compliance Risk', value: 'Low' },
    ],
    subProjects: groupByStage(mortgageProjects),
  },
  {
    id: 'manufacturing',
    number: '/0.2',
    name: 'Manufacturing Intelligence',
    tagline: 'AI decision support for order acceptance and production planning',
    description:
      'Manufacturing Intelligence helps filter manufacturers and media converters make faster, safer order-acceptance decisions. It evaluates roll stock availability, plant-level capacity, existing customer commitments, transport feasibility, and cut-width constraints to recommend the best production and allocation plan with clear trade-offs.',
    features: [
      'Unified visibility into roll stock, plant availability, and existing commitments',
      'Optimization-driven planning across capacity, transport, yield, and conversion constraints',
      'Shortage and surplus detection across plants and production lines',
      'Plain-language recommendations that explain trade-offs, risks, and next-best options',
    ],
    metrics: [
      { label: 'Decision Speed', value: 'Faster' },
      { label: 'Material Waste', value: 'Lower' },
      { label: 'OTIF Performance', value: 'Improved' },
    ],
    processes: [
      {
        name: 'Monitor',
        detail:
          'Track roll stock, plant-level inventory, in-transit material, and reserved capacity across locations in real time.',
      },
      {
        name: 'Evaluate',
        detail:
          'Assess incoming demand against long-horizon contracts, existing SLAs, conversion constraints, cut-width feasibility, and transport trade-offs.',
      },
      {
        name: 'Recommend',
        detail:
          'Generate mathematically valid fulfillment scenarios, rank the best options, and explain the recommended plan in plain language for faster business decisions.',
      },
    ],
  },
];

// ─── Product Data ────────────────────────────────────────────────────────────

const products: Product[] = [
  {
    id: 'budhi',
    number: '/0.3',
    name: 'Budhi AI',
    tagline: 'AI document intelligence for enterprise workflows',
    description:
      'Budhi AI is an enterprise document intelligence platform that extracts, classifies, and reasons over unstructured documents. Built for organizations that need to process large volumes of complex content with precision and speed.',
    features: [
      'Multi-format document ingestion (PDF, images, scanned docs)',
      'Contextual extraction with domain-specific models',
      'Automated classification and routing workflows',
      'Enterprise-grade security and compliance',
    ],
    link: { label: 'Visit Budhi AI', href: 'https://thebudhi.ai' },
    appStoreUrl: 'https://apps.apple.com/in/app/budhi-ai/id6755378258',
  },
  {
    id: 'satya',
    number: '/0.4',
    name: 'Satya AI',
    tagline: 'Trusted health AI for everyday medical guidance',
    description:
      'Satya AI is a trusted health AI assistant for everyday medical guidance. Built with advanced safeguards for health information, it cross-references medical knowledge and catches potential errors so you can trust what you read.',
    features: [
      'Voice-first interface \u2014 no tech skills needed, works for all ages',
      'Lab results explained in simple language (cholesterol, diabetes markers, etc.)',
      'Medication clarity \u2014 what it does, how to take it, side effects',
      'Doctor visit prep \u2014 transcribe concerns, get English summary for appointments',
    ],
    link: { label: 'Visit Satya AI', href: 'https://satya-ai.com' },
    appStoreUrl: 'https://apps.apple.com/in/app/satya-ai/id6755962816',
  },
  {
    id: 'governance',
    number: '/0.5',
    name: 'Enterprise Governance',
    tagline: 'AI governance, risk, and compliance operations',
    description:
      'Enterprise Governance is a unified platform for monitoring AI systems, enforcing policies, managing risk, and maintaining audit readiness across the enterprise. It helps regulated organizations operationalize governance with real-time oversight, traceability, and compliance workflows.',
    features: [
      'Automated policy monitoring and control enforcement',
      'Real-time risk scoring, escalation, and exception tracking',
      'Compliance workflows with centralized audit evidence',
      'Governed oversight for AI systems, decisions, and operational processes',
    ],
  },
  {
    id: 'enterprise-search',
    number: '/0.6',
    name: 'Enterprise Search',
    tagline: 'AI-powered search, chat, and knowledge intelligence',
    description:
      'Enterprise Search is a knowledge intelligence platform that turns scattered documents into governed, searchable organizational memory. It ingests unstructured content, extracts relationships and meaning, and helps teams find answers through natural language search, chat, and knowledge graph exploration.',
    features: [
      'AI-powered document processing, classification, and entity extraction',
      'Hybrid search across semantic, keyword, and graph-based retrieval',
      'Conversational Q&A over enterprise knowledge with citations',
      'Role-based access control with governed discovery across teams and departments',
    ],
  },
];

// ─── Agent Data ──────────────────────────────────────────────────────────────

const agents: Agent[] = [
  {
    id: 'marketing',
    number: '/0.7',
    name: 'Marketing Agent',
    tagline: 'AI agent for marketing content and campaign execution',
    description:
      'The Marketing Agent is an AI agent for planning and generating marketing content across campaigns and channels. It leverages historical and real-time business data, analyzes requirements, incorporates market trends, and produces contextually aware materials aligned with current objectives.',
    features: [
      'Learns from previous successful campaigns',
      'Incorporates current market trends and business context',
      'Produces brand-aligned content at scale',
    ],
    metrics: [
      { label: 'Time Saved', value: '75%' },
      { label: 'Engagement', value: '+40%' },
      { label: 'Brand Consistency', value: '100%' },
    ],
  },
  {
    id: 'procurement',
    number: '/0.8',
    name: 'Procurement Agent',
    tagline: 'Streamline vendor evaluation with AI',
    description:
      'Automate vendor communication and provide objective, data-driven analysis of RFQ responses. Automatically distributes RFQs, evaluates responses against a rubric, and delivers ranked vendor recommendations with detailed justification.',
    features: [
      'Automated RFQ distribution to pre-defined vendors',
      'Response evaluation against pre-defined rubrics',
      'Top 3 vendor recommendation with justification',
    ],
    metrics: [
      { label: 'Processing Time', value: '-60%' },
      { label: 'Cost Savings', value: '15%' },
      { label: 'Vendor Quality', value: 'High' },
    ],
  },
  {
    id: 'retail',
    number: '/0.9',
    name: 'Retail Automation',
    tagline: 'Modernize sales with WhatsApp integration',
    description:
      'Accelerate the sales cycle and automate the collection and processing of secondary sales data. Sales reps capture orders via WhatsApp, the agent generates formal documents, and OCR processes retailer invoices directly into the ERP.',
    features: [
      'WhatsApp-based order capture and processing',
      'OCR-powered invoice ingestion into ERP',
      'Rewards-linked data sharing incentives',
    ],
    metrics: [
      { label: 'Sales Cycle', value: '-40%' },
      { label: 'Data Accuracy', value: '99%' },
      { label: 'Adoption Rate', value: 'High' },
    ],
  },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

function ChapterHeader({ number, label }: { number: string; label: string }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div ref={ref} className="py-16">
      <motion.div
        className="border-t border-border"
        initial={{ scaleX: 0 }}
        animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        style={{ transformOrigin: 'left' }}
      />
      <div className="flex items-baseline gap-4 pt-6">
        <span className="font-mono text-sm text-muted-foreground">{number}</span>
        <span className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground">
          {label}
        </span>
      </div>
    </div>
  );
}

function IndustryRow({
  item,
  isExpanded,
  onToggle,
  expandedStage,
  onToggleStage,
}: {
  item: Industry;
  isExpanded: boolean;
  onToggle: () => void;
  expandedStage: string | null;
  onToggleStage: (stage: string) => void;
}) {
  const { ref, isVisible } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={cn(
        'border-b border-border transition-opacity duration-600',
        isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'
      )}
    >
      <button
        onClick={onToggle}
        className="w-full text-left py-8 md:py-10 group cursor-pointer"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-6 md:gap-10 min-w-0">
            <span className="font-mono text-sm text-muted-foreground shrink-0">
              {item.number}
            </span>
            <h3 className="text-[clamp(2rem,5vw,4.5rem)] font-heading font-normal tracking-[-1.5px] leading-[1.1] transition-transform duration-300 group-hover:translate-x-1">
              {item.name}
            </h3>
          </div>
          <div className="flex items-center gap-6 shrink-0">
            <span className="hidden md:block text-sm text-muted-foreground max-w-[200px] text-right">
              {item.tagline}
            </span>
            <ArrowUpRight
              className={cn(
                'w-5 h-5 text-muted-foreground transition-transform duration-300',
                isExpanded && 'rotate-45'
              )}
            />
          </div>
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-10 md:pb-14 pl-0 md:pl-[calc(theme(spacing.10)+theme(spacing.6))]">
              <div className="max-w-3xl">
                <p className="text-muted-foreground leading-relaxed mb-8">
                  {item.description}
                </p>

                {/* Inline metrics */}
                <div className="flex flex-wrap gap-x-10 gap-y-3 mb-8">
                  {item.metrics.map((m, i) => (
                    <div key={i} className="flex items-baseline gap-2">
                      <span className="text-2xl font-heading font-normal text-foreground">
                        {m.value}
                      </span>
                      <span className="text-xs text-muted-foreground uppercase tracking-wider">
                        {m.label}
                      </span>
                    </div>
                  ))}
                </div>

                <ul className="space-y-2 mb-8">
                  {item.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-foreground">
                      <span className="text-muted-foreground mt-1.5 block w-1 h-1 rounded-full bg-muted-foreground shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Nested accordion: Mortgage stages */}
                {item.subProjects && (
                  <div className="mt-10">
                    <h4 className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
                      19 Automation Projects by Stage
                    </h4>
                    <div className="space-y-1">
                      {item.subProjects.map((group) => (
                        <div key={group.stage} className="border-l border-border pl-4">
                          <button
                            onClick={() => onToggleStage(group.stage)}
                            className="w-full text-left py-2 flex items-center gap-2 group/stage cursor-pointer"
                          >
                            <ChevronRight
                              className={cn(
                                'w-3.5 h-3.5 text-muted-foreground transition-transform duration-200',
                                expandedStage === group.stage && 'rotate-90'
                              )}
                            />
                            <span className="font-mono text-sm text-foreground">
                              {group.stage}
                            </span>
                            <span className="text-xs text-muted-foreground ml-auto font-mono">
                              {group.projects.length}
                            </span>
                          </button>
                          <AnimatePresence>
                            {expandedStage === group.stage && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                                className="overflow-hidden"
                              >
                                <ul className="space-y-1 pb-2 pl-5">
                                  {group.projects.map((p, i) => (
                                    <li
                                      key={i}
                                      className="text-sm text-muted-foreground border-l border-border pl-4 py-1"
                                    >
                                      <span className="text-foreground">{p.name}</span>
                                      {' \u2014 '}
                                      {p.summary}
                                    </li>
                                  ))}
                                </ul>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Nested accordion: Manufacturing processes */}
                {item.processes && (
                  <div className="mt-10">
                    <h4 className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
                      Process Phases
                    </h4>
                    <div className="space-y-1">
                      {item.processes.map((proc) => (
                        <div key={proc.name} className="border-l border-border pl-4">
                          <button
                            onClick={() => onToggleStage(proc.name)}
                            className="w-full text-left py-2 flex items-center gap-2 cursor-pointer"
                          >
                            <ChevronRight
                              className={cn(
                                'w-3.5 h-3.5 text-muted-foreground transition-transform duration-200',
                                expandedStage === proc.name && 'rotate-90'
                              )}
                            />
                            <span className="font-mono text-sm text-foreground">
                              {proc.name}
                            </span>
                          </button>
                          <AnimatePresence>
                            {expandedStage === proc.name && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                                className="overflow-hidden"
                              >
                                <p className="text-sm text-muted-foreground pl-5 pb-3 leading-relaxed">
                                  {proc.detail}
                                </p>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ProductRow({
  item,
  isExpanded,
  onToggle,
}: {
  item: Product;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { ref, isVisible } = useScrollReveal();
  const navigate = useNavigate();

  return (
    <div
      ref={ref}
      className={cn(
        'border-b border-border transition-opacity duration-600',
        isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'
      )}
    >
      <button
        onClick={onToggle}
        className="w-full text-left py-8 md:py-10 group cursor-pointer"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-6 md:gap-10 min-w-0">
            <span className="font-mono text-sm text-muted-foreground shrink-0">
              {item.number}
            </span>
            <h3 className="text-[clamp(2rem,5vw,4.5rem)] font-heading font-normal tracking-[-1.5px] leading-[1.1] transition-transform duration-300 group-hover:translate-x-1">
              {item.name}
            </h3>
          </div>
          <div className="flex items-center gap-6 shrink-0">
            <span className="hidden md:block text-sm text-muted-foreground max-w-[200px] text-right">
              {item.tagline}
            </span>
            <ArrowUpRight
              className={cn(
                'w-5 h-5 text-muted-foreground transition-transform duration-300',
                isExpanded && 'rotate-45'
              )}
            />
          </div>
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-10 md:pb-14 pl-0 md:pl-[calc(theme(spacing.10)+theme(spacing.6))]">
              <div className="max-w-2xl">
                <p className="text-muted-foreground leading-relaxed mb-8">
                  {item.description}
                </p>
                <ul className="space-y-2 mb-8">
                  {item.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-foreground">
                      <span className="text-muted-foreground mt-1.5 block w-1 h-1 rounded-full bg-muted-foreground shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap items-center gap-4">
                  {item.id === 'governance' || item.id === 'enterprise-search' ? (
                    <button
                      onClick={() => navigate('/contact-us')}
                      className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent transition-colors"
                    >
                      Request a Demo
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    item.link && (
                      <a
                        href={item.link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent transition-colors"
                      >
                        {item.link.label}
                        <ArrowUpRight className="w-4 h-4" />
                      </a>
                    )
                  )}
                  {item.appStoreUrl && (
                    <a
                      href={item.appStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent transition-colors"
                    >
                      <Smartphone className="w-4 h-4" />
                      Download on the App Store
                    </a>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function AgentRow({
  item,
  isExpanded,
  onToggle,
}: {
  item: Agent;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { ref, isVisible } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={cn(
        'border-b border-border transition-opacity duration-600',
        isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'
      )}
    >
      <button
        onClick={onToggle}
        className="w-full text-left py-8 md:py-10 group cursor-pointer"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-6 md:gap-10 min-w-0">
            <span className="font-mono text-sm text-muted-foreground shrink-0">
              {item.number}
            </span>
            <h3 className="text-[clamp(2rem,5vw,4.5rem)] font-heading font-normal tracking-[-1.5px] leading-[1.1] transition-transform duration-300 group-hover:translate-x-1">
              {item.name}
            </h3>
          </div>
          <div className="flex items-center gap-6 shrink-0">
            <span className="hidden md:block text-sm text-muted-foreground max-w-[200px] text-right">
              {item.tagline}
            </span>
            <ArrowUpRight
              className={cn(
                'w-5 h-5 text-muted-foreground transition-transform duration-300',
                isExpanded && 'rotate-45'
              )}
            />
          </div>
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-10 md:pb-14 pl-0 md:pl-[calc(theme(spacing.10)+theme(spacing.6))]">
              <div className="max-w-3xl">
                <p className="text-muted-foreground leading-relaxed mb-8">
                  {item.description}
                </p>

                <div className="flex flex-wrap gap-x-10 gap-y-3 mb-8">
                  {item.metrics.map((m, i) => (
                    <div key={i} className="flex items-baseline gap-2">
                      <span className="text-2xl font-heading font-normal text-foreground">
                        {m.value}
                      </span>
                      <span className="text-xs text-muted-foreground uppercase tracking-wider">
                        {m.label}
                      </span>
                    </div>
                  ))}
                </div>

                <ul className="space-y-2">
                  {item.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-foreground">
                      <span className="text-muted-foreground mt-1.5 block w-1 h-1 rounded-full bg-muted-foreground shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

const ProductsPageV2 = () => {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedStage, setExpandedStage] = useState<string | null>(null);
  const ctaSection = useScrollReveal();

  const toggle = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
    setExpandedStage(null);
  };

  const toggleStage = (stage: string) =>
    setExpandedStage((prev) => (prev === stage ? null : stage));

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero */}
      <section className="max-w-[1200px] mx-auto pt-44 pb-20 px-8">
        <motion.h1
          className="font-heading text-[clamp(2.4rem,5.5vw,4.2rem)] font-normal tracking-[-2px] leading-[1.05] mb-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
        >
          Enterprise AI, built for <span className="italic">real workflows</span>
        </motion.h1>
        <motion.p
          className="text-muted-foreground text-lg max-w-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.5,
            delay: 0.1,
            ease: [0.25, 0.1, 0.25, 1],
          }}
        >
          From industry-specific automations to AI products and agents, we build systems that reduce manual work, improve speed, and fit how your business actually operates.
        </motion.p>
      </section>

      {/* Chapter 1: Solutions */}
      <section className="max-w-[1200px] mx-auto px-8">
        <ChapterHeader number="/01" label="Solutions" />
        <p className="text-sm text-muted-foreground max-w-xl mb-6">
          Industry-specific AI automation solutions
        </p>
        {industries.map((industry) => (
          <IndustryRow
            key={industry.id}
            item={industry}
            isExpanded={expandedId === industry.id}
            onToggle={() => toggle(industry.id)}
            expandedStage={expandedId === industry.id ? expandedStage : null}
            onToggleStage={toggleStage}
          />
        ))}
      </section>

      {/* Chapter 2: Products */}
      <section className="max-w-[1200px] mx-auto px-8">
        <ChapterHeader number="/02" label="Products" />
        <p className="text-sm text-muted-foreground max-w-xl mb-6">
          AI products for document intelligence, health guidance, governance, and enterprise knowledge access.
        </p>
        {products.map((product) => (
          <ProductRow
            key={product.id}
            item={product}
            isExpanded={expandedId === product.id}
            onToggle={() => toggle(product.id)}
          />
        ))}
      </section>

      {/* Chapter 3: Agents */}
      <section className="max-w-[1200px] mx-auto px-8">
        <ChapterHeader number="/03" label="Agents" />
        <p className="text-sm text-muted-foreground max-w-xl mb-6">
          Customizable AI agents for marketing, procurement, and retail workflow automation.
        </p>
        {agents.map((agent) => (
          <AgentRow
            key={agent.id}
            item={agent}
            isExpanded={expandedId === agent.id}
            onToggle={() => toggle(agent.id)}
          />
        ))}
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-[1200px] mx-auto px-8 mt-20 pb-10 md:pb-16 scroll-mt-32">
        <div className="relative rounded-none border border-overlay/[0.08] bg-overlay/[0.01]">
          <span className="absolute top-3 left-4 text-overlay/[0.15] text-sm font-mono select-none z-20">
            +
          </span>
          <span className="absolute bottom-3 right-4 text-overlay/[0.15] text-sm font-mono select-none z-20">
            +
          </span>

          <div className="relative z-10 px-6 md:px-14 lg:px-20 pt-14 md:pt-16 pb-10 md:pb-14">
            <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-20">
              <div>
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-overlay/[0.08] bg-overlay/[0.03] backdrop-blur-sm mb-7">
                  <span className="text-[13px] font-medium text-muted-foreground">FAQ</span>
                </div>
                <h2 className="text-[2rem] sm:text-[2.4rem] font-semibold tracking-[-0.04em] leading-[1] mb-4">
                  Frequently asked
                  <br />
                  <span className="text-gradient-primary">questions</span>
                </h2>
                <p className="text-[15px] text-muted-foreground font-light leading-relaxed">
                  Quick answers about Antz AI and how we partner with teams.
                </p>
              </div>

              <div>
                {faqData.map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1200px] mx-auto px-8 py-32">
        <div
          ref={ctaSection.ref}
          className={cn(
            'max-w-3xl mx-auto text-center p-12 rounded-xl border border-border bg-background transition-all duration-600',
            ctaSection.isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-4'
          )}
        >
          <h2 className="text-2xl md:text-3xl font-semibold mb-4 tracking-[-0.04em]">
            Need an AI solution for your workflow?
          </h2>
          <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
            Tell us about your process, and we'll identify where automation, AI products, or agents can create the most impact.
          </p>
          <motion.button
            onClick={() => navigate('/contact-us')}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3 rounded-lg font-medium text-sm hover:opacity-90 transition-opacity shadow-[0_0_24px_rgba(124,58,237,0.25)]"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          >
            Get in Touch
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPageV2;
